import { Provider, signal } from '@angular/core';

import { ATTENTION_PROJECTIONS, AttentionProjection } from '@shared/services/attention/attention-projections';

import { GYM_RAIL } from './gym.routes';

export { GYM_RAIL } from './gym.routes';

/* What the gym store last counted, published here so the ROOT projection reads it without importing
   the store — which would pull the whole gym into the app's initial chunk (the 7 MB budget; the
   hotel's, the salon's and the grocery's pattern). The DS's first cut injected the store from the
   projection and every register booted offline (`gym-store.ref.ts`); here nothing is built at all.
   Until the gym has been opened this session there is nothing counted, and no badge. Nothing here
   may import the store or the records (`gym.routes.ts` is root-light too). */
const RENEW = signal(0);
const BOOKED = signal(0);
const TABS = signal(0);
const KIT = signal(0);

/** What the store counted — the four badged rows (GM-19). */
export interface GymBadgeCounts {
  /** Memberships due or expired: somebody to renew — Members. */
  readonly renew: number;
  /** One-to-ones still `booked` today — Timetable. */
  readonly booked: number;
  /** Tabs at the club's limit or over: somebody to settle with — Bar & wallet. */
  readonly tabs: number;
  /** Machines not `ok` (faulty or overdue a service) — Lockers & kit. */
  readonly kit: number;
}

/** The store's one write. */
export function publishGymBadges(c: GymBadgeCounts): void {
  if (RENEW() !== c.renew) RENEW.set(c.renew);
  if (BOOKED() !== c.booked) BOOKED.set(c.booked);
  if (TABS() !== c.tabs) TABS.set(c.tabs);
  if (KIT() !== c.kit) KIT.set(c.kit);
}

/**
 * The gym's rail badges (the DS `gym.attention.ts`, GM-19, D441) — *work waiting on a person today,
 * never a total*. Each rule carries the `capKey` of the screen it points at (the DS's: `members`,
 * `classes`, `wallet`, `lockers`), so a badge cannot advertise work behind a capability the tenant
 * does not have.
 */
export function gymAttentionProjections(): readonly AttentionProjection[] {
  return [
    {
      rule: { id: 'gym.members.renew', moduleId: 'gym', capKey: 'members', railItemId: GYM_RAIL.members, enabled: true, scope: 'site' },
      count: RENEW.asReadonly(),
    },
    {
      rule: { id: 'gym.classes.booked', moduleId: 'gym', capKey: 'classes', railItemId: GYM_RAIL.timetable, enabled: true, scope: 'site' },
      count: BOOKED.asReadonly(),
    },
    {
      rule: { id: 'gym.wallet.tabs', moduleId: 'gym', capKey: 'wallet', railItemId: GYM_RAIL.bar, enabled: true, scope: 'site' },
      count: TABS.asReadonly(),
    },
    {
      rule: { id: 'gym.lockers.kit', moduleId: 'gym', capKey: 'lockers', railItemId: GYM_RAIL.assets, enabled: true, scope: 'site' },
      count: KIT.asReadonly(),
    },
  ];
}

/** Register the gym's projections at the root (AppModule), so the shell can show them. */
export function provideGymAttention(): Provider[] {
  return [{ provide: ATTENTION_PROJECTIONS, useValue: gymAttentionProjections, multi: true }];
}
